import React, { useState, useEffect } from 'react';
import {
    TextField,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Grid,
    Paper,
    Typography,
    Chip,
    Box,
    IconButton
} from '@mui/material';
import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material';

interface TimeOffsetOption {
    value: number;
    label: string;
}

interface TimeOffsetCheckboxGroupProps {
    value: number[];
    onChange: (offsets: number[]) => void;
    label?: string;
}

const TimeOffsetCheckboxGroup: React.FC<TimeOffsetCheckboxGroupProps> = ({
    value,
    onChange,
    label = "Reminder offsets"
}) => {
    const [offsets, setOffsets] = useState<number[]>(value || []);
    const [customOffset, setCustomOffset] = useState<string>('');

    // Predefined time offsets in minutes
    const predefinedOffsets: TimeOffsetOption[] = [
        { value: -10080, label: '7 days before' },
        { value: -4320, label: '3 days before' },
        { value: -1440, label: '1 day before' },
        { value: 0, label: 'On date due' },
    ];

    useEffect(() => {
        setOffsets(value || []);
    }, [value]);

    const handleCheckboxChange = (offsetValue: number): void => {
        const newOffsets = offsets.includes(offsetValue)
            ? offsets.filter(v => v !== offsetValue)
            : [...offsets, offsetValue].sort((a, b) => a - b);

        setOffsets(newOffsets);
        onChange(newOffsets);
    };

    const handleAddCustomOffset = (): void => {
        const numValue = Number(customOffset);
        if (!isNaN(numValue) && customOffset.trim() !== '' && !offsets.includes(numValue)) {
            const newOffsets = [...offsets, numValue].sort((a, b) => a - b);
            setOffsets(newOffsets);
            onChange(newOffsets);
            setCustomOffset('');
        }
    };

    const handleRemoveOffset = (offsetToRemove: number): void => {
        const newOffsets = offsets.filter(v => v !== offsetToRemove);
        setOffsets(newOffsets);
        onChange(newOffsets);
    };

    const handleClearAll = (): void => {
        setOffsets([]);
        onChange([]);
    };

    const formatOffset = (offset: number): string => {
        if (offset === 0) return 'Due time';

        const absMinutes = Math.abs(offset);
        const isNegative = offset < 0;

        if (absMinutes >= 10080) {
            const weeks = absMinutes / 10080;
            return `${weeks} week${weeks !== 1 ? 's' : ''} ${isNegative ? 'before' : 'after'}`;
        } else if (absMinutes >= 1440) {
            const days = absMinutes / 1440;
            return `${days} day${days !== 1 ? 's' : ''} ${isNegative ? 'before' : 'after'}`;
        } else if (absMinutes >= 60) {
            const hours = absMinutes / 60;
            return `${hours} hour${hours !== 1 ? 's' : ''} ${isNegative ? 'before' : 'after'}`;
        } else {
            return `${absMinutes} minute${absMinutes !== 1 ? 's' : ''} ${isNegative ? 'before' : 'after'}`;
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent): void => {
        if (e.key === 'Enter') {
            handleAddCustomOffset();
        }
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Typography variant="subtitle1" gutterBottom>
                {label}
            </Typography>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                    Selected reminders:
                </Typography>

                {offsets.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No reminders selected
                    </Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        {offsets.map((offset) => (
                            <Chip
                                key={offset}
                                label={`${formatOffset(offset)}`}
                                onDelete={() => handleRemoveOffset(offset)}
                                color="primary"
                                variant="outlined"
                            />
                        ))}
                    </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        {offsets.length} reminder{offsets.length !== 1 ? 's' : ''} selected
                    </Typography>
                    {offsets.length > 0 && (
                        <Typography
                            variant="body2"
                            color="primary"
                            onClick={handleClearAll}
                            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        >
                            Clear all
                        </Typography>
                    )}
                </Box>
            </Paper>

            <Grid container spacing={2}>
                <Grid sx={{ xs: 12, md: 8 }}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Reminder Options:
                        </Typography>
                        <FormGroup>
                            <Grid container spacing={1}>
                                {predefinedOffsets.map((option) => (
                                    <Grid sx={{ xs: 12, sm: 6 }} key={option.value}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={offsets.includes(option.value)}
                                                    onChange={() => handleCheckboxChange(option.value)}
                                                    size="small"
                                                />
                                            }
                                            label={option.label}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </FormGroup>
                    </Paper>
                </Grid>
            </Grid>

        </Box>
    );
};



export default TimeOffsetCheckboxGroup;